export type BeatLineTurnContext = {
  state: Record<string, any>;
  updateUI?: (skipTimelineRebuild?: boolean) => void;
  updateScores?: () => void;
  endGame?: (winner: number) => void;
  beatlineSetBonusOpen?: (open: boolean) => void;
  closeModal?: (id: string) => void;
  confetti?: (...args: any[]) => void;
  triggerWrongEffect?: () => void;
  showBonusModal?: (headerText: string, subMsg: string, status: string, opts?: Record<string, any>) => void;
  t?: (key: string, fallback?: string) => string;
};

export type BeatLineTurnResult = { handled: boolean; reason?: string };

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

function player(state: Record<string, any>, slot: number): Record<string, any> {
  const p = state[`p${slot}`];
  if (!p || typeof p !== 'object') {
    state[`p${slot}`] = { score: 0, tokens: 0, timeline: [] };
  }
  return state[`p${slot}`];
}

function normalModeOnly(state: Record<string, any>): boolean {
  return !(state.perfectOrder || state.timeAttack || state.survival || state.speedrun);
}

export function updateScoresFromTypeScript(ctx: BeatLineTurnContext): BeatLineTurnResult {
  const { state } = ctx;
  if (!state) return { handled: false, reason: 'missing-state' };

  // Solo/special modes have custom score rules in the golden runtime.
  if (!normalModeOnly(state)) return { handled: false, reason: 'complex-mode' };

  const count = parseInt(String(state.playerCount || 1), 10) || 1;
  for (let slot = 1; slot <= count; slot += 1) {
    const p = player(state, slot);
    p.score = Array.isArray(p.timeline) ? p.timeline.length : 0;
  }
  safeCall(ctx.updateUI);
  return { handled: true, reason: 'normal-update-scores' };
}

export function nextTurnFromTypeScript(ctx: BeatLineTurnContext): BeatLineTurnResult {
  const { state } = ctx;
  if (!state) return { handled: false, reason: 'missing-state' };

  // The normal multiplayer next-turn behaviour is exact and safe to own.
  // Special modes keep their own legacy flow until those modes are mapped separately.
  if (!normalModeOnly(state)) return { handled: false, reason: 'complex-mode' };

  state.activeCard = null;
  state.turn = (parseInt(String(state.turn || 1), 10) || 1) + 1;
  const playerCount = parseInt(String(state.playerCount || 1), 10) || 1;
  if (state.turn > playerCount) state.turn = 1;

  safeCall(ctx.updateUI);

  const checkBtn = document.getElementById('btn-check');
  const nextBtn = document.getElementById('btn-next');
  const deckZone = document.getElementById('deck-zone');
  if (checkBtn) checkBtn.style.display = 'none';
  if (nextBtn) nextBtn.style.display = 'flex';
  if (deckZone) deckZone.innerHTML = '';

  return { handled: true, reason: 'normal-next-turn' };
}

export function resolveTurnFromTypeScript(bonus: boolean, ctx: BeatLineTurnContext): BeatLineTurnResult {
  const { state } = ctx;
  if (!state) return { handled: false, reason: 'missing-state' };

  // Perfect/timeattack/survival/speedrun use timeAttackContinue() in the original runtime.
  if (!normalModeOnly(state)) return { handled: false, reason: 'complex-mode' };

  const modal = document.getElementById('bonus-modal');
  if (modal) modal.style.display = 'none';
  safeCall(ctx.beatlineSetBonusOpen, false);

  const turnSlot = parseInt(String(state.turn || 1), 10) || 1;
  const p = player(state, turnSlot);
  if (bonus && (parseInt(String(p.tokens || 0), 10) || 0) < 5) {
    p.tokens = (parseInt(String(p.tokens || 0), 10) || 0) + 1;
  }

  const scoreResult = updateScoresFromTypeScript(ctx);
  if (!scoreResult.handled) safeCall(ctx.updateScores);

  let winner = 0;
  const winTarget = parseInt(String(state.winTarget || 10), 10) || 10;
  const count = parseInt(String(state.playerCount || 1), 10) || 1;
  for (let slot = 1; slot <= count; slot += 1) {
    const pSlot = player(state, slot);
    if ((parseInt(String(pSlot.score || 0), 10) || 0) >= winTarget) winner = slot;
  }

  if (winner > 0) {
    safeCall(ctx.endGame, winner);
  } else {
    const nextResult = nextTurnFromTypeScript(ctx);
    if (!nextResult.handled) {
      // Let the legacy runtime decide for any mode we do not own yet.
      return { handled: false, reason: `next-turn-${nextResult.reason || 'fallback'}` };
    }
  }

  return { handled: true, reason: 'normal-resolve-turn' };
}

export function finalizeTurnFromTypeScript(playerId: number, valid: boolean, ctx: BeatLineTurnContext): BeatLineTurnResult {
  const { state } = ctx;
  if (!state) return { handled: false, reason: 'missing-state' };

  // Keep special scoring/lives/penalties in the exact legacy implementation for now.
  if (!normalModeOnly(state)) return { handled: false, reason: 'complex-mode' };

  const pZone = document.getElementById(`timeline-p${playerId}`);
  const vis = document.getElementById('vis-anim');
  const placement = state.tempActivePlacement || {};
  const pendingCard = placement.card as HTMLElement | undefined;

  if (pendingCard && pendingCard.classList) pendingCard.classList.add('revealed');
  if (vis) vis.style.visibility = 'hidden';

  const t = typeof ctx.t === 'function' ? ctx.t : ((key: string, fallback = key) => fallback);

  if (valid) {
    const p = player(state, playerId);
    if (!Array.isArray(p.timeline)) p.timeline = [];
    p.timeline.push(state.activeCard);

    const fx = () => safeCall(ctx.confetti, { particleCount: 150, spread: 70, origin: { y: 0.6 }, zIndex: 9999999 });
    safeCall(ctx.showBonusModal, 'CORRECT!', '', 'correct', { effectFn: fx, bonusPlayerId: playerId });
  } else {
    const removeCardNow = () => {
      if (pendingCard && pZone && pendingCard.parentNode === pZone) {
        pZone.removeChild(pendingCard);
      }
    };
    const fx = () => {
      removeCardNow();
      safeCall(ctx.triggerWrongEffect);
    };
    safeCall(ctx.showBonusModal, t('wrong'), '', 'wrong', { effectFn: fx, bonusPlayerId: playerId });
  }

  return { handled: true, reason: 'normal-finalize-turn' };
}
